import React from 'react';
import { useMutation } from '@tanstack/react-query';
import FileSelectorBrowser from '../../../shared/components/FileSelectorBrowser';
import { SelectedFile } from '../../../types/FileManagerTypes';
import { UploadFlows } from '../../../enums/uploadFlows';
import { useLegendForm, useLegendUI } from '../../../context/MapLegendContext';
import { useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import FileManagerService from '../../../services/FileManager/fileManager.service';

export interface AddFromDataPortalProps {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
}

const AddFromDataPortal: React.FC<AddFromDataPortalProps> = ({ open, onClose, onBack }) => {
  const { setFolderId, setFolderLocation, setFolderS3Key, setUploadFileType, setUploadedFiles, setDatasetId } = useLegendForm();
  const { setLayerUploadFlow, setModalVisible } = useLegendUI();
  const { setState } = useLayerProperitesContext();
  const { resolveDatasetForFiles } = FileManagerService();
  const { mutateAsync: resolveDataset } = useMutation(resolveDatasetForFiles());

  const handleSelect = async (file: SelectedFile) => {
    setFolderId(file.folderId);
    setFolderLocation(file.folderName);
    setFolderS3Key(file.s3Key);
    // Inferred from the file's extension; this flow has no type selector.
    setUploadFileType(file.fileType);
    // executeSave derives the upload extension from these names, so a
    // shapefile set resolves to its .shp.
    setUploadedFiles(file.files);

    setState((prev) => ({
      ...prev,
      layerTitle: file.baseName,
      uniqueLayerName: file.baseName,
      folderId: file.folderId,
    }));

    // This file was already ingested once (it's browsable in the Data
    // Portal). Resolve its Dataset so saveLayerToGeoserver reuses that same
    // PostGIS table instead of re-ingesting the file from S3 into a fresh,
    // independent one — a fresh ingest would drop any fields added directly
    // to an existing layer's live table via the Attribute Table. Best-effort:
    // if this fails, the layer still saves, just without table reuse.
    try {
      const { datasetId } = await resolveDataset({
        folderId: file.folderId,
        fileNames: file.files.map((f) => f.name),
      });
      setDatasetId(datasetId ?? undefined);
    } catch {
      setDatasetId(undefined);
    }

    setLayerUploadFlow(UploadFlows.DATA_PORTAL);
    setModalVisible(true);
  };

  return <FileSelectorBrowser open={open} onClose={onClose} onBack={onBack} onSelect={handleSelect} variant="light" />;
};

export default AddFromDataPortal;
